import { beginFrames, renderFrame } from './frame.js';
import { exportName } from './filename.js';
import { S } from '../state/store.js';
import { settleAt } from '../state/timeline.js';
import { toast } from '../ui/toast.js';
import { drawWatermarkPattern } from './watermark.js';
import { EXPORT_COST, videoWatermarkOn, watermarkExports } from '../services/limits.js';
import { motionActive, revalidateMotion } from '../services/motion.js';
import { creditBalance, setCreditBalance } from '../services/entitlements.js';
import { requireClient } from '../services/supabase.js';
import { track } from '../services/analytics.js';

/* ═══════════════════════════════════════════════════════════
   EXPORT — one still frame
   ═══════════════════════════════════════════════════════════ */

/**
 * @param {{watermark?: boolean}} [opts]
 *   An explicit true/false skips credit spending entirely and just honours
 *   the override. Left undefined (the normal path), a watermark-free still
 *   costs EXPORT_COST.still — spent via the consume_export_credits() RPC,
 *   which is the actual authority and does the guard and the subtraction in
 *   one statement. A real race (another tab spending the last credits first)
 *   falls back to the watermark on this same already-rendered frame rather
 *   than failing the export outright.
 *
 *   Be clear-eyed about what this is: the render happens in the browser, so
 *   the pixels are already on the user's machine and a mark can be removed.
 *   It's a nudge, not a lock — same as before, just backed now by a balance
 *   a client can spend but never grant itself (0004_billing.sql's RLS).
 */
export async function exportPNG(opts = {}) {
  /* The render itself is shared with the video exporter (export/frame.js) —
     a still and a frame of the video of the same scene have to be the same
     picture, and the only way to guarantee that is for it to be the same
     code. */
  const pass = beginFrames();
  /* Waited for, not assumed. A video object's frame comes from a decoder, so
     without this the still would capture whatever frame happened to be on its
     canvas from the last preview repaint — usually the right one, occasionally
     not, and never reliably. settleAt is the same call the video exporter
     makes, so a still and the corresponding frame of the MP4 are the same
     pixels by construction rather than by promise. */
  let out;
  try {
    await settleAt(S.playhead);
    /* opts.size mirrors exportMP4's own — the whole-wall export of a screen
       array asks for it, so a still and a frame of the video of the same wall
       come out the same size as well as the same picture. */
    out = opts.size ? renderFrame(null, opts.size.w, opts.size.h) : renderFrame();
  } finally {
    pass.end();           // always: the pass holds the live preview until it ends
  }

  const watermark = await resolveWatermark(opts.watermark);
  if (watermark) await stampWatermark(out);

  /* The size ACTUALLY delivered, not S.res. They are the same on every path
     that does not pass opts.size — but the whole-wall export of an LCD video wall
     does, and it was naming a 1920 px file after the wall's own 5823 px and
     saying so in the toast as well. A filename that misreports its contents is
     worse than an ugly one: it is the thing an installer reads to decide which
     file goes where. */
  const outW = out.width, outH = out.height;
  out.toBlob(b => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(b);
    /* 'Wall' is derived rather than passed in, so the name follows the FILE
       rather than whoever asked for it. opts.size is only ever given by the
       whole-wall export (see the note above on outW/outH); the mockup passes a
       size too but names itself explicitly, and an explicit kind always wins. */
    a.download = exportName({ kind: opts.kind || (opts.size ? 'Wall' : ''),
                              spec: `${outW}x${outH}`, ext: 'png' });
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    toast(`Exported ${outW} × ${outH} PNG, rendered at ${pass.w} × ${pass.h}.${pass.note}`, !!pass.note);
    track('export_png', {
      watermarked: watermark, cost: watermark ? 0 : EXPORT_COST.still,
      w: outW, h: outH, supersample: Math.round(pass.w / Math.max(1, outW))
    });
  }, 'image/png');
}

/**
 * Decide whether this export carries the mark, spending a credit if that is
 * what it takes. Shared with the video exporter so the two can never end up
 * on different rules — and spent ONCE per export, not once per frame, which
 * is why it is a separate step from the render rather than folded into it.
 *
 * @param {boolean} [override]  an explicit choice skips the whole question
 * @param {number} [cost]  credits this export is worth — see EXPORT_COST. A
 *        still by default, since that is what most callers are.
 */
export async function resolveWatermark(override, cost = EXPORT_COST.still) {
  if (override != null) return override;
  /* A free export is a clean one. Stills cost nothing, so this is what makes
     every still path come out unmarked — the single PNG, a corner or wall's
     panel set, and the on-site mockup — without each of them having to know
     it. There is also nothing to charge: passing 0 to the spend RPC raises,
     deliberately, because an export that costs nothing should never have
     reached it. */
  if (cost <= 0) return false;
  /* The mark is on unless it was switched off on the Export screen — see
     services/limits.js. Decided here, once, so every video path (the single
     MP4, a corner or wall's panel set, the on-site clip) honours it without
     each caller being handed an option to thread through. Free, and nothing
     to spend or to ask the server: somebody who has chosen the watermark has
     chosen not to pay. */
  if (videoWatermarkOn()) return true;
  /* Ask the server again, HERE, because this is the one moment being wrong
     costs something: an unmarked file leaving on a licence that has lapsed or
     been revoked. motionActive() is a cache refreshed on auth:changed and at
     the known expiry — neither of which covers an admin removing a licence
     while the tab is open. One round trip, once per export, against an encode
     that takes seconds.

     Failure is not fatal: a network hiccup leaves the cached answer in place
     rather than watermarking someone who has paid (see loadMotionLicence's
     error path). The cache is only ever set from a server reply, so the worst
     it can be is out of date, and the scheduled re-check corrects it. */
  await revalidateMotion().catch(() => {});
  /* And a Motion licence, for the identical reason stated just above: it is
     the paid tier, so the export is already paid for. Checked BEFORE the
     spend, not left to watermarkExports() at the bottom — reaching that
     line means passing through the spending branch first, which would charge
     a licensed customer for something their licence already covers. */
  if (motionActive()) return false;
  /* The amount is checked and subtracted in ONE statement server-side
     (consume_export_credits, 0026), so two tabs exporting at once cannot both
     pass a check and take the balance below zero. Nothing is asked of the
     balance here first: a local check would be a second opinion that can
     disagree with the one that counts, and the round trip is the same either
     way. */
  if (creditBalance() > 0) {
    const { data, error } = await requireClient().rpc('consume_export_credits', { p_amount: cost });
    if (!error && data != null) { setCreditBalance(data); return false; }
    /* Not enough, or the call failed. Name the price and what is there, or
       "no credit available" reads as a fault on an account that simply has
       four of the ten this costs. */
    toast(`This export costs ${cost} credit${cost === 1 ? '' : 's'} and you have `
      + `${creditBalance()} — exported with a watermark instead.`, true);
    return true;
  }
  return watermarkExports();
}

/** @param {{w:number,h:number}} [sizeRef] size the mark as if the frame were
 *  this instead — a corner screen's two panels share one mark size.
 *  @param {{seconds:number, seed:number}} [motion] on a video frame: where in
 *  the clip this is, and which export it belongs to. The mark moves every few
 *  seconds for video and stays put for a still — see export/watermark.js. */
export async function stampWatermark(canvas, sizeRef, motion) {
  await drawWatermarkPattern(canvas.getContext('2d'), canvas.width, canvas.height,
                             sizeRef && sizeRef.w, sizeRef && sizeRef.h, motion);
}
